import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useStyleContext } from '../context/StyleContext';
import { CodeModal } from 'components/UI/Modals';

export const demoVariablesJSONString = `{\n\t"--ts-var-button--secondary-background": "#F0EBFF",\n\t"--ts-var-button--secondary--hover-background": "#E3D9FC",\n\t"--ts-var-root-background": "#F7F5FF"\n}`;
export const placeholderText = `Paste your ThoughtSpot styling variables in JSON. Example:\n\n${demoVariablesJSONString}`;

const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const usePasteJSONModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { updateVariableInBulk } = useStyleContext();
  const setMessageHandlerRef = useRef<(message: string) => void>();

  const closePasteModal = useCallback(() => {
    setIsVisible(false);
  }, []);

  const openPasteModal = useCallback(() => {
    setIsVisible(true);
  }, []);

  const PasteModal = useMemo(() => {
    const Component = React.memo(
      ({ setMessageHandler }: { setMessageHandler: (message: string) => void }) => {
        const [userJSON, setUserJSON] = useState('');
        const [isError, setIsError] = useState(false);
        const [errorMessage, setErrorMessage] = useState('Please enter styling variables in JSON');

        // Store the setMessageHandler in a ref to avoid dependency changes
        useEffect(() => {
          setMessageHandlerRef.current = setMessageHandler;
        }, [setMessageHandler]);

        const handleChange = useCallback((text: string) => {
          setUserJSON(text);
          if (text && text.trim() !== '') {
            if (isJsonString(text)) {
              setIsError(false);
            } else {
              setIsError(true);
              setErrorMessage(
                'Your JSON contains invalid characters. Please review and try again.'
              );
            }
          } else {
            setIsError(false);
          }
        }, []);

        const handleSubmitTheme = useCallback(
          (text: string) => {
            try {
              if (!text || text.trim() === '') {
                setIsError(true);
                setErrorMessage('Please enter styling variables in JSON');
                return;
              }
              const data = JSON.parse(text);
              if (!isError) {
                updateVariableInBulk(data);
                closePasteModal();
                setMessageHandlerRef.current?.('JSON Uploaded!');
              }
            } catch (e) {
              console.error('JSON parse error:', e);
            }
          },
          [updateVariableInBulk, closePasteModal]
        );

        const handleSubmitClick = useCallback(() => {
          handleSubmitTheme(userJSON);
        }, [handleSubmitTheme, userJSON]);

        const handleTextChange = useCallback(
          (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            handleChange(e.target.value);
          },
          [handleChange]
        );

        const confirmButtonProps = useMemo(
          () => ({
            title: 'Import',
            onClick: handleSubmitClick,
          }),
          [handleSubmitClick]
        );

        const dismissButtonProps = useMemo(
          () => ({
            title: 'Cancel',
            onClick: closePasteModal,
          }),
          [closePasteModal]
        );

        const headerProps = useMemo(
          () => ({
            heading: 'Paste your ThoughtSpot styling variables in JSON',
          }),
          []
        );

        const textAreaProps = useMemo(
          () => ({
            placeholder: placeholderText,
            rows: 20,
            readOnly: false,
            onChange: handleTextChange,
          }),
          [handleTextChange]
        );

        const errorProps = useMemo(
          () => ({
            isError: isError,
            errorMessage: errorMessage,
          }),
          [isError]
        );

        return (
          <CodeModal
            isVisible={isVisible}
            closeModal={closePasteModal}
            confirmButtonProps={confirmButtonProps}
            dismissButtonProps={dismissButtonProps}
            content={userJSON}
            title={'Import JSON'}
            headerProps={headerProps}
            textAreaProps={textAreaProps}
            errorProps={errorProps}
            codeType="JSON"
          />
        );
      }
    );
    Component.displayName = 'PasteModal';
    return Component;
  }, [isVisible, closePasteModal, updateVariableInBulk]);

  return {
    openPasteModal: openPasteModal,
    PasteModal: PasteModal,
  };
};
